<template>
  <div class="incoherence-range-step">
    <!-- Local preview of the file about to be uploaded (object URL owned by the parent). -->
    <video
      v-show="!previewFailed"
      ref="video"
      class="incoherence-range-step__video"
      :src="videoUrl"
      controls
      preload="metadata"
      playsinline
      @loadedmetadata="onVideoMetadata"
      @durationchange="onVideoMetadata"
      @timeupdate="onVideoTimeUpdate"
      @error="onVideoError"
    ></video>

    <v-switch
      :input-value="value.noIncoherence"
      label="Truthful video (no incoherence)"
      dense
      hide-details
      class="mt-3"
      @change="onNoIncoherenceChange"
    />

    <template v-if="durationMs > 0">
      <!-- Same selection mode as the analysis screen: two clicks on the timeline
           (lie start, then lie end), with the lie drawn as a red range overlay. -->
      <div
        ref="track"
        class="incoherence-step-timeline mt-5"
        :class="{ 'incoherence-step-timeline--picking': isPicking }"
        @click="onTimelineClick"
      >
        <div class="incoherence-step-timeline__progress" :style="{ width: thumbLeftPercent }"></div>
        <div
          v-if="incoherenceOverlayStyle"
          class="incoherence-range-marker"
          :style="incoherenceOverlayStyle"
          title="Lie"
        ></div>
        <div
          v-if="pickPendingStyle"
          class="incoherence-step-timeline__pending"
          :style="pickPendingStyle"
        ></div>
        <div class="incoherence-step-timeline__thumb" :style="{ left: thumbLeftPercent }"></div>
      </div>

      <p v-if="isPicking" class="caption warning--text text--darken-2 mt-2 mb-0">
        <v-icon x-small color="warning darken-2">mdi-cursor-default-click-outline</v-icon>
        {{ pickHint }} (Esc cancels)
      </p>

      <div class="d-flex align-center flex-wrap mt-2">
        <v-chip small outlined color="primary" :disabled="value.noIncoherence">
          Start {{ formatMs(value.startMs) }}
        </v-chip>
        <v-icon small class="mx-1">mdi-arrow-right</v-icon>
        <v-chip small outlined color="primary" :disabled="value.noIncoherence">
          End {{ formatMs(value.endMs) }}
        </v-chip>
        <v-btn
          small
          text
          color="primary"
          class="ml-2"
          :disabled="value.noIncoherence"
          @click="isPicking ? cancelPick() : startPick()"
        >
          <v-icon small left>{{ isPicking ? 'mdi-close' : 'mdi-pencil' }}</v-icon>
          {{ isPicking ? 'Cancel' : hasRange ? 'Edit lie range' : 'Mark lie range' }}
        </v-btn>
        <v-spacer />
        <span class="caption grey--text text--darken-1">
          Video duration {{ formatMs(durationMs) }}
        </span>
      </div>

      <p v-if="showRangeError" class="caption error--text mt-2 mb-0">
        The lie end must be after its start.
      </p>
      <p
        v-else-if="showUntouchedHint && !isPicking"
        class="caption grey--text text--darken-1 mt-2 mb-0"
      >
        Click the timeline to mark the lie start and end, or switch on "Truthful video (no incoherence)".
      </p>
    </template>

    <!-- Fallback when the browser cannot decode the file (e.g. AVI): manual range entry. -->
    <template v-else-if="previewFailed">
      <v-alert dense outlined type="warning" class="caption mt-3 mb-0">
        This video cannot be previewed in the browser. Enter the lie range manually (check the times
        in an external player), or switch on "Truthful video (no incoherence)".
      </v-alert>
      <div class="d-flex flex-wrap mt-3 incoherence-range-step__manual">
        <v-text-field
          :value="value.startMs / 1000"
          label="Lie start (seconds)"
          type="number"
          min="0"
          step="0.1"
          dense
          outlined
          hide-details
          :disabled="value.noIncoherence"
          class="incoherence-range-step__manual-field"
          @input="onManualStartInput"
        />
        <v-text-field
          :value="value.endMs / 1000"
          label="Lie end (seconds)"
          type="number"
          min="0"
          step="0.1"
          dense
          outlined
          hide-details
          :disabled="value.noIncoherence"
          class="incoherence-range-step__manual-field"
          @input="onManualEndInput"
        />
      </div>
      <p v-if="showRangeError" class="caption error--text mt-2 mb-0">
        The lie end must be after its start.
      </p>
    </template>

    <p v-else class="caption grey--text text--darken-1 mt-2 mb-0">Loading video metadata...</p>
  </div>
</template>

<script>
/**
 * One step of the pre-upload lie labeling stepper: plays the local video file
 * and lets the user mark the lie temporal range (or flag the video as incoherence-free).
 * The range is selected exactly like on the analysis screen: a pick mode where
 * the first timeline click sets the lie start and the second sets the lie end
 * (clicks are swapped when reversed; Esc cancels). Outside pick mode a timeline
 * click seeks the video. When the browser cannot decode the file (e.g. AVI),
 * the preview is replaced with manual numeric inputs so the lie can still be
 * labeled.
 *
 * v-model value shape: { fileKey, noIncoherence, startMs, endMs, durationMs, previewFailed }.
 * The component never mutates the value; it emits a patched copy via `input`.
 */
const METADATA_TIMEOUT_MS = 10000;

export default {
  name: 'IncoherenceRangeStep',
  props: {
    value: { type: Object, required: true },
    /** Object URL of the local file. Created and revoked by the parent. */
    videoUrl: { type: String, required: true },
    /** Whether this step is the active one; the video is paused when it stops being active. */
    active: { type: Boolean, default: false },
  },
  data() {
    return {
      metadataTimeoutId: null,
      infiniteDurationFixApplied: false,
      infiniteDurationRestored: false,
      /** Two-click pick mode state, mirroring the analysis screen flow. */
      pickActive: false,
      pickStartMs: null,
      /** Pick mode is auto-started once per video (when it is still unlabeled). */
      autoPickStarted: false,
      currentTimeMs: 0,
    };
  },
  computed: {
    durationMs() {
      const duration = Number(this.value?.durationMs);
      return Number.isFinite(duration) && duration > 0 ? duration : 0;
    },
    previewFailed() {
      return Boolean(this.value?.previewFailed) && this.durationMs <= 0;
    },
    isUntouched() {
      return this.value.startMs === 0 && this.value.endMs === 0;
    },
    hasRange() {
      return !this.isUntouched && this.value.endMs > this.value.startMs;
    },
    isPicking() {
      return this.pickActive && !this.value.noIncoherence && this.durationMs > 0;
    },
    pickHint() {
      return this.pickStartMs == null
        ? 'Click the timeline to set the lie start'
        : 'Click the timeline to set the lie end';
    },
    thumbLeftPercent() {
      if (this.durationMs <= 0) return '0%';
      const ratio = Math.min(1, Math.max(0, this.currentTimeMs / this.durationMs));
      return `${ratio * 100}%`;
    },
    /** Same visual as the analysis player: red translucent block spanning the lie. */
    incoherenceOverlayStyle() {
      if (this.value.noIncoherence || !this.hasRange || this.durationMs <= 0) return null;
      const left = Math.min(1, Math.max(0, this.value.startMs / this.durationMs));
      const right = Math.min(1, Math.max(0, this.value.endMs / this.durationMs));
      return { left: `${left * 100}%`, width: `${Math.max(0, right - left) * 100}%` };
    },
    /** Thin marker on the pending start click while waiting for the end click. */
    pickPendingStyle() {
      if (!this.isPicking || this.pickStartMs == null || this.durationMs <= 0) return null;
      const ratio = Math.min(1, Math.max(0, this.pickStartMs / this.durationMs));
      return { left: `${ratio * 100}%` };
    },
    showRangeError() {
      if (this.value.noIncoherence || this.isUntouched) return false;
      return this.value.endMs <= this.value.startMs;
    },
    showUntouchedHint() {
      return !this.value.noIncoherence && this.isUntouched;
    },
  },
  watch: {
    active(isActive) {
      if (!isActive) {
        this.pauseVideo();
        this.cancelPick();
      }
    },
  },
  mounted() {
    // Some undecodable files never fire `error` nor `loadedmetadata`; fall back
    // to manual range entry when the metadata never arrives.
    this.metadataTimeoutId = setTimeout(() => {
      this.metadataTimeoutId = null;
      if (this.durationMs <= 0 && !this.value.previewFailed) {
        this.emitPatch({ previewFailed: true });
      }
    }, METADATA_TIMEOUT_MS);
  },
  beforeDestroy() {
    this.clearMetadataTimeout();
    this.cancelPick();
    this.pauseVideo();
  },
  methods: {
    emitPatch(patch) {
      this.$emit('input', { ...this.value, ...patch });
    },
    onVideoMetadata(event) {
      const video = event?.target;
      const durationSeconds = Number(video?.duration);
      if (durationSeconds === Infinity) {
        // MediaRecorder-produced WebM reports duration = Infinity until the
        // browser is forced to compute it by seeking far past the end; the
        // corrected duration arrives through a later `durationchange`.
        if (video && !this.infiniteDurationFixApplied) {
          this.infiniteDurationFixApplied = true;
          video.currentTime = Number.MAX_SAFE_INTEGER;
        }
        return;
      }
      if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return;
      this.clearMetadataTimeout();
      if (video && this.infiniteDurationFixApplied && !this.infiniteDurationRestored) {
        this.infiniteDurationRestored = true;
        video.currentTime = 0;
      }
      const durationMs = Math.floor(durationSeconds * 1000);
      this.maybeAutoStartPick();
      if (durationMs === Number(this.value.durationMs)) return;
      const patch = { durationMs };
      if (this.value.previewFailed) patch.previewFailed = false;
      if (Number(this.value.startMs) > durationMs) patch.startMs = durationMs;
      if (Number(this.value.endMs) > durationMs) patch.endMs = durationMs;
      this.emitPatch(patch);
    },
    onVideoError() {
      // The browser cannot decode this file (e.g. AVI in an HTML5 element).
      this.clearMetadataTimeout();
      if (this.durationMs > 0 || this.value.previewFailed) return;
      this.emitPatch({ previewFailed: true });
    },
    clearMetadataTimeout() {
      if (this.metadataTimeoutId) {
        clearTimeout(this.metadataTimeoutId);
        this.metadataTimeoutId = null;
      }
    },
    onVideoTimeUpdate(event) {
      const ms = Math.round(Number(event?.target?.currentTime) * 1000);
      if (Number.isFinite(ms) && ms >= 0) this.currentTimeMs = ms;
    },
    /** Unlabeled videos drop straight into pick mode so the step starts ready to mark. */
    maybeAutoStartPick() {
      if (this.autoPickStarted || this.value.noIncoherence || !this.isUntouched) return;
      this.autoPickStarted = true;
      this.startPick();
    },
    startPick() {
      this.pickStartMs = null;
      this.pickActive = true;
      document.addEventListener('keydown', this.onPickKeydown);
    },
    cancelPick() {
      this.pickActive = false;
      this.pickStartMs = null;
      document.removeEventListener('keydown', this.onPickKeydown);
    },
    onPickKeydown(event) {
      if (event.key === 'Escape') this.cancelPick();
    },
    onTimelineClick(event) {
      const track = this.$refs.track;
      if (!track || this.durationMs <= 0) return;
      const rect = track.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      const ms = Math.round(ratio * this.durationMs);
      if (this.isPicking) {
        this.applyPickedMs(ms);
        return;
      }
      // Outside pick mode the timeline behaves like the player's: click seeks.
      const video = this.$refs.video;
      if (video) video.currentTime = ms / 1000;
      this.currentTimeMs = ms;
    },
    /** Same semantics as the analysis screen: first click = start, second = end (swapped if reversed). */
    applyPickedMs(ms) {
      if (this.pickStartMs == null) {
        this.pickStartMs = ms;
        return;
      }
      const startMs = Math.min(this.pickStartMs, ms);
      const endMs = Math.max(this.pickStartMs, ms);
      if (endMs === startMs) return; // need a non-empty range; keep waiting for the end click
      this.cancelPick();
      this.emitPatch({ startMs, endMs });
    },
    onManualStartInput(value) {
      const startMs = this.parseSecondsToMs(value);
      if (startMs == null) return;
      this.emitPatch({ startMs });
    },
    onManualEndInput(value) {
      const endMs = this.parseSecondsToMs(value);
      if (endMs == null) return;
      this.emitPatch({ endMs });
    },
    parseSecondsToMs(value) {
      const seconds = Number(String(value ?? '').trim());
      if (!Number.isFinite(seconds) || seconds < 0) return null;
      return Math.round(seconds * 1000);
    },
    onNoIncoherenceChange(checked) {
      const noIncoherence = Boolean(checked);
      if (noIncoherence) {
        this.cancelPick();
      } else if (this.isUntouched && this.durationMs > 0) {
        this.startPick();
      }
      this.emitPatch({ noIncoherence });
    },
    pauseVideo() {
      const video = this.$refs.video;
      if (video && !video.paused) video.pause();
    },
    formatMs(ms) {
      const totalSeconds = Math.max(0, Math.round(Number(ms || 0) / 1000));
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },
  },
};
</script>

<style scoped>
.incoherence-range-step__video {
  display: block;
  width: 100%;
  max-height: 320px;
  background: #000;
  border-radius: 8px;
  /* The global stylesheet mirrors every <video> (rotateY(180deg), meant for webcam
     self-view); uploaded files must play unmirrored. */
  transform: none;
}

/* Lightweight clone of the analysis player timeline, restyled for the light dialog. */
.incoherence-step-timeline {
  position: relative;
  height: 10px;
  border-radius: 5px;
  background: rgba(0, 0, 0, 0.12);
  cursor: pointer;
}

.incoherence-step-timeline--picking {
  cursor: crosshair;
  outline: 1px dashed rgba(255, 160, 0, 0.8);
  outline-offset: 3px;
}

.incoherence-step-timeline__progress {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-radius: 5px;
  background: rgba(25, 118, 210, 0.35);
  pointer-events: none;
}

.incoherence-step-timeline__thumb {
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #1976d2;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.incoherence-step-timeline__pending {
  position: absolute;
  top: -4px;
  bottom: -4px;
  width: 2px;
  background: rgba(229, 57, 53, 0.9);
  transform: translateX(-50%);
  pointer-events: none;
}

/* Same visual language as the player's lie overlay (.incoherence-range-marker). */
.incoherence-range-marker {
  position: absolute;
  top: -3px;
  bottom: -3px;
  background: rgba(229, 57, 53, 0.4);
  border: 1px solid rgba(229, 57, 53, 0.9);
  border-radius: 2px;
  pointer-events: none;
}

.incoherence-range-step__manual {
  gap: 12px;
}

.incoherence-range-step__manual-field {
  max-width: 200px;
}
</style>
